import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadDrinkCollection, loadDrinkCollectionSummary, loadDrinkDetail } from '../../api/drinkCollectionApi';
import { DRINK_DISCOVERY_EVENT } from '../../utils/drinkDiscovery';

const INITIAL_FILTERS = { search: '', status: '', category: '', brand: '', packaging: '', alcohol: '', sort: 'product', page: 1, per_page: 40 };

export function collectionTitle(playerName) {
  const name = String(playerName || '').trim();
  if (!name) return 'Käpt’n Korks Getränke-Logbuch';
  const possessive = /[sxzß]$/i.test(name) ? `${name}’` : `${name}s`;
  return `${possessive} Getränke-Logbuch`;
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(String(value).replace(' ', 'T') + 'Z');
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('de-DE');
}

function amountFromDeposit(value) {
  const match = String(value || '').match(/\d+(?:[,.]\d+)?/);
  const amount = match ? Number(match[0].replace(',', '.')) : 0;
  return Number.isFinite(amount) && amount > 0 ? amount : 0;
}

function DrinkImage({ drink, large = false, muted = false }) {
  const [failed, setFailed] = useState(false);
  return (
    <div className={`pfp-collection-image ${large ? 'is-large' : ''} ${muted ? 'is-undiscovered' : ''}`}>
      {!failed && drink.image_url ? (
        <img src={drink.image_url} alt="" loading="lazy" onError={() => setFailed(true)} />
      ) : <span aria-hidden="true">🥤</span>}
    </div>
  );
}

function ProgressHeader({ summary, user }) {
  const percent = Number(summary?.percent || 0);
  return (
    <header className="pfp-collection-hero">
      <div>
        <span className="pfp-collection-eyebrow">Deine persönliche Sammlung</span>
        <h1>{collectionTitle(user?.name)}</h1>
        <p>Deine entdeckten Getränke</p>
      </div>
      <div className="pfp-collection-progress" aria-label={`${percent} Prozent gesammelt`}>
        <strong>{summary?.discovered || 0} <small>von {summary?.total || 0}</small></strong>
        <div className="pfp-collection-progress__track"><span style={{ width: `${Math.min(100, percent)}%` }} /></div>
        <span>{percent}% · {summary?.discovered_this_month || 0} neu in diesem Monat</span>
      </div>
    </header>
  );
}

function FilterBar({ value, metadata, onChange }) {
  const update = (key, next) => onChange({ ...value, [key]: next, page: 1 });
  return (
    <section className="pfp-collection-filters" aria-label="Getränke filtern">
      <label className="pfp-collection-search">
        <span>Getränk suchen</span>
        <input value={value.search} onChange={event => update('search', event.target.value)} placeholder="Name, EAN oder Kategorie" />
      </label>
      <div className="pfp-collection-quick-filters" role="group" aria-label="Schnellfilter">
        {[['', 'Alle'], ['discovered', 'Entdeckt'], ['undiscovered', 'Noch offen'], ['new', 'Neu entdeckt']].map(([key, label]) => (
          <button key={label} type="button" className={value.status === key ? 'is-active' : ''} onClick={() => update('status', key)}>{label}</button>
        ))}
      </div>
      <div className="pfp-collection-selects">
        <label>Kategorie<select value={value.category} onChange={event => update('category', event.target.value)}><option value="">Alle</option>{metadata?.categories?.map(item => <option key={item.id} value={item.id}>{item.name} ({item.count})</option>)}</select></label>
        <label>Verpackung<select value={value.packaging} onChange={event => update('packaging', event.target.value)}><option value="">Alle</option>{metadata?.packaging?.map(item => <option key={item.key} value={item.key}>{item.label}</option>)}</select></label>
        <label>Alkohol<select value={value.alcohol} onChange={event => update('alcohol', event.target.value)}><option value="">Alle</option>{metadata?.alcohol?.map(item => <option key={item.key} value={item.key}>{item.label}</option>)}</select></label>
        {metadata?.brand_available ? <label>Marke<select value={value.brand} onChange={event => update('brand', event.target.value)}><option value="">Alle Marken</option>{metadata.brands?.map(item => <option key={item.id} value={item.id}>{item.name} ({item.count})</option>)}</select></label> : null}
        <label>Sortierung<select value={value.sort} onChange={event => update('sort', event.target.value)}><option value="product">Produktname</option>{metadata?.brand_available ? <option value="brand">Marke</option> : null}<option value="last">Zuletzt gesehen</option><option value="first">Zuerst entdeckt</option><option value="sightings">Sichtungen</option></select></label>
      </div>
    </section>
  );
}

function DrinkCard({ drink, onOpen }) {
  return (
    <button type="button" className={`pfp-collection-card ${drink.discovered ? 'is-discovered' : ''}`} onClick={() => onOpen(drink.id)}>
      <DrinkImage drink={drink} muted={!drink.discovered} />
      <span className="pfp-collection-card__body">
        <span className="pfp-collection-card__category">{drink.category?.name || 'Ohne Kategorie'}</span>
        {drink.brand?.name ? <span className="pfp-collection-card__brand">{drink.brand.name}</span> : null}
        <strong>{drink.name}</strong>
        <span>{[drink.volume, drink.packaging_label, drink.deposit].filter(Boolean).join(' · ')}</span>
        {drink.discovered ? (
          <span className="pfp-collection-status is-found">✓ Entdeckt · {drink.sightings} {drink.sightings === 1 ? 'Sichtung' : 'Sichtungen'}</span>
        ) : <span className="pfp-collection-status">Noch nicht entdeckt</span>}
      </span>
    </button>
  );
}

function DetailPanel({ drink, loading, error, onClose, onAdd, onMark }) {
  const closeRef = useRef(null);
  useEffect(() => { closeRef.current?.focus(); }, [drink?.id, loading]);
  if (!drink && !loading && !error) return null;
  return (
    <div className="pfp-collection-detail-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
      <aside className="pfp-collection-detail" role="dialog" aria-modal="true" aria-labelledby="pfp-drink-detail-title">
        <button ref={closeRef} type="button" className="pfp-collection-detail__close" onClick={onClose} aria-label="Details schließen">×</button>
        {loading ? <div className="pfp-collection-state">Getränk wird geladen …</div> : null}
        {error ? <div className="pfp-collection-state is-error">{error}</div> : null}
        {drink ? <>
          <DrinkImage drink={drink} large muted={!drink.discovered} />
          <span className="pfp-collection-eyebrow">{drink.category?.name || 'Getränk'}</span>
          <h2 id="pfp-drink-detail-title">{drink.name}</h2>
          <dl>
            <div><dt>Marke</dt><dd>{drink.brand?.name || 'Noch nicht gepflegt'}</dd></div>
            <div><dt>EAN</dt><dd>{drink.ean || '—'}</dd></div>
            <div><dt>Volumen</dt><dd>{drink.volume || '—'}</dd></div>
            <div><dt>Verpackung</dt><dd>{drink.packaging_label}</dd></div>
            <div><dt>Pfand</dt><dd>{drink.deposit || '—'}</dd></div>
            <div><dt>Alkohol</dt><dd>{drink.alcohol_status || '—'} {drink.alcohol_content || ''}</dd></div>
          </dl>
          {drink.discovered ? <div className="pfp-collection-detail__history"><strong>Deine Entdeckung</strong><span>Erstmals: {formatDate(drink.first_discovered_at)}</span><span>Zuletzt: {formatDate(drink.last_seen_at)}</span><span>Sichtungen: {drink.sightings}</span></div> : <p className="pfp-collection-status">Dieses Getränk wartet noch auf seine erste Sichtung.</p>}
          {drink.description ? <div className="pfp-collection-description" dangerouslySetInnerHTML={{ __html: drink.description }} /> : null}
          <div className="pfp-collection-detail__actions">
            <button type="button" className="pfp-collection-primary-button" onClick={() => onAdd(drink)}>Als Beute eintragen</button>
            <button type="button" className="pfp-collection-secondary-button" onClick={() => onMark(drink)}>Für Sammler markieren</button>
            {drink.product_url ? <a href={drink.product_url} target="_blank" rel="noopener noreferrer">Vollständige Getränkeseite</a> : null}
          </div>
        </> : null}
      </aside>
    </div>
  );
}

export default function CollectionPage({ add, user }) {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [data, setData] = useState({ items: [], pagination: {}, filters: {} });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const requestSequence = useRef(0);

  useEffect(() => {
    const controller = new AbortController();
    const sequence = ++requestSequence.current;
    const timer = window.setTimeout(() => {
      setLoading(true); setError('');
      loadDrinkCollection(filters, controller.signal)
        .then(next => { if (sequence === requestSequence.current) setData(next); })
        .catch(err => { if (err.name !== 'AbortError' && sequence === requestSequence.current) setError(err.message); })
        .finally(() => { if (sequence === requestSequence.current) setLoading(false); });
    }, filters.search ? 220 : 0);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [filters, refreshKey]);

  useEffect(() => { loadDrinkCollectionSummary().then(setSummary).catch(() => setSummary(null)); }, [data.items, refreshKey]);
  useEffect(() => {
    const refresh = () => setRefreshKey(current => current + 1);
    window.addEventListener(DRINK_DISCOVERY_EVENT, refresh);
    return () => window.removeEventListener(DRINK_DISCOVERY_EVENT, refresh);
  }, []);
  useEffect(() => {
    const onKey = event => { if (event.key === 'Escape') setSelected(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const openDetail = id => {
    setSelected(null); setDetailError(''); setDetailLoading(true);
    loadDrinkDetail(id).then(setSelected).catch(err => setDetailError(err.message)).finally(() => setDetailLoading(false));
  };

  const addDrink = drink => {
    const amount = amountFromDeposit(drink.deposit);
    add(amount, {
      barcode: drink.ean, product_name: drink.name, volume: drink.volume,
      description: drink.description || '', pfand_info: drink.deposit,
      activity_source: 'collection', source_id: drink.id,
      internal_url: drink.product_url || '',
    });
    setSelected(null);
  };

  const pages = Number(data.pagination?.pages || 0);
  return (
    <main className="pfp-collection-page">
      <ProgressHeader summary={summary} user={user} />
      <FilterBar value={filters} metadata={data.filters} onChange={setFilters} />
      {loading ? <div className="pfp-collection-state">Logbuch wird geladen …</div> : null}
      {error ? <div className="pfp-collection-state is-error"><strong>Das Logbuch konnte nicht geladen werden.</strong><span>{error}</span><button type="button" onClick={() => setFilters(current => ({ ...current }))}>Erneut versuchen</button></div> : null}
      {!loading && !error && data.items.length === 0 ? <div className="pfp-collection-state"><strong>Keine Getränke gefunden.</strong><span>Ändere die Filter oder den Suchbegriff.</span></div> : null}
      <section className="pfp-collection-grid" aria-live="polite">
        {data.items.map(drink => <DrinkCard key={drink.id} drink={drink} onOpen={openDetail} />)}
      </section>
      {pages > 1 ? <nav className="pfp-collection-pagination" aria-label="Seitennavigation"><button type="button" disabled={filters.page <= 1} onClick={() => setFilters(current => ({ ...current, page: current.page - 1 }))}>Zurück</button><span>Seite {filters.page} von {pages}</span><button type="button" disabled={filters.page >= pages} onClick={() => setFilters(current => ({ ...current, page: current.page + 1 }))}>Weiter</button></nav> : null}
      <DetailPanel drink={selected} loading={detailLoading} error={detailError} onClose={() => { setSelected(null); setDetailError(''); }} onAdd={addDrink} onMark={drink => navigate('/quick-mark', { state: { drink } })} />
    </main>
  );
}
